import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { invitationService } from '@/services';
import type { Notification } from '@/types';

/**
 * Fetches pending invitations for the current user and exposes them
 * as Notification-shaped objects so they can be merged into the
 * NotificationCenter alongside regular notifications.
 */
export function useInvitations() {
  const [invitationNotifications, setInvitationNotifications] = useState<
    Notification[]
  >([]);
  const [loading, setLoading] = useState(true);

  const fetchInvitations = useCallback(async () => {
    try {
      const invitations = await invitationService.getMyInvitations();
      const mapped: Notification[] = invitations.map((inv) => {
        const householdLabel = inv.household_name?.trim() || 'a household';
        return {
          id: `inv-${inv.id}`,
          household_id: inv.household_id ?? '',
          recipient_user_id: '',
          actor_id: inv.inviter_id ?? null,
          type: 'invitation' as const,
          title: `You've been invited to ${householdLabel}`,
          body: `Tap Accept to join, or Decline to ignore.`,
          deep_link: null,
          metadata: {
            invitation_id: inv.id,
            household_name: householdLabel,
          },
          created_at: inv.created_at,
          read_at: null,
          dismissed_at: null,
        };
      });
      setInvitationNotifications(mapped);
    } catch {
      /* silent — user may not be authenticated yet */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  // Listen for realtime changes on caregiver_invitations table
  useEffect(() => {
    const channel = supabase
      .channel('invitations-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'caregiver_invitations',
        },
        () => {
          fetchInvitations();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchInvitations]);

  const acceptInvitation = useCallback(async (invitationId: string) => {
    await invitationService.acceptInvitation(invitationId);
    // Remove from local list immediately
    setInvitationNotifications((prev) =>
      prev.filter((n) => n.metadata?.invitation_id !== invitationId),
    );
  }, []);

  const declineInvitation = useCallback(async (invitationId: string) => {
    await invitationService.declineInvitation(invitationId);
    setInvitationNotifications((prev) =>
      prev.filter((n) => n.metadata?.invitation_id !== invitationId),
    );
  }, []);

  return {
    invitationNotifications,
    loading,
    acceptInvitation,
    declineInvitation,
    refresh: fetchInvitations,
  };
}
